import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { ApiError, del, errorMessage, get, patch, post, put } from '../api/client';
import type { BikeModel, Issue, ModelListItem } from '../api/types';
import { useMe } from '../auth';
import { IconAlert, IconCopy, IconEye, IconEyeOff, IconPlus, IconStar, IconTrash } from '../components/icons';
import { DragHandle, SortableList } from '../components/sortable';
import { Field, Modal, PageHead, slugify, Spinner, useFeedback } from '../components/ui';
import { useT } from '../i18n';

interface ModelsResponse {
  items: ModelListItem[];
  catalogIssues: Issue[];
}

export function Models() {
  const { t, tn, tr } = useT();
  const qc = useQueryClient();
  const { data: me } = useMe();
  const { toast, confirm } = useFeedback();
  const [creating, setCreating] = useState<{ from?: ModelListItem } | null>(null);
  const { data, isPending } = useQuery({ queryKey: ['models'], queryFn: () => get<ModelsResponse>('/admin/models') });

  const refresh = () => qc.invalidateQueries({ queryKey: ['models'] });
  // Los reordenamientos van en fila (scope) y solo el último pinta la respuesta: arrastrar
  // dos veces seguido no hace saltar la lista a un orden intermedio.
  const reorder = useMutation({
    mutationKey: ['models', 'order'],
    scope: { id: 'models-order' },
    mutationFn: (ids: string[]) => put<ModelsResponse>('/admin/models/order', { ids }),
    onSuccess: (res) => {
      if (qc.isMutating({ mutationKey: ['models', 'order'] }) <= 1) qc.setQueryData(['models'], res);
    },
    onError: (e) => {
      toast(errorMessage(e), 'error');
      void refresh();
    },
  });

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      toast(ok);
      await refresh();
      void qc.invalidateQueries({ queryKey: ['overview'] });
    } catch (e) {
      toast(errorMessage(e), 'error');
    }
  };

  const items = data?.items ?? [];

  return (
    <>
      <PageHead
        kicker={t('Catálogo')}
        title={t('Motos')}
        description={t('Arrastra para cambiar el orden del catálogo y del carrusel del home. La destacada es la que el carrusel muestra primero.')}
      >
        <button className="btn btn-primary" onClick={() => setCreating({})}>
          <IconPlus /> {t('Nueva moto')}
        </button>
      </PageHead>

      {data?.catalogIssues.map((i) => (
        <div key={i.message} className={`notice ${i.level === 'error' ? 'error' : 'warn'}`} style={{ marginBottom: 12 }}>
          <IconAlert width={18} /> {i.message}
        </div>
      ))}

      {isPending ? (
        <Spinner />
      ) : items.length === 0 ? (
        <div className="panel empty-state">{t('Todavía no hay motos.')}</div>
      ) : (
        <div className="panel" style={{ overflow: 'hidden' }}>
          <SortableList
            items={items}
            getId={(m) => m.id}
            onReorder={(next) => {
              qc.setQueryData<ModelsResponse>(['models'], (old) => old && { ...old, items: next });
              reorder.mutate(next.map((m) => m.id));
            }}
          >
            {(m, _i, handle) => (
              <div className="list-item">
                <DragHandle {...handle} />
                <div className="thumb checker">{m.thumbnail && <img src={m.thumbnail.url} alt="" />}</div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="row row-wrap" style={{ gap: 8 }}>
                    <Link to={`/motos/${m.id}`} style={{ fontWeight: 700, fontSize: 16, textDecoration: 'none' }}>
                      {m.name}
                    </Link>
                    {m.featured && (
                      <span className="badge red">
                        <IconStar width={12} /> {t('Destacada')}
                      </span>
                    )}
                    {m.visible ? <span className="badge ok">{t('Visible')}</span> : <span className="badge">{t('Oculta')}</span>}
                    {m.errors > 0 && <span className="badge red">{t('{n} por completar', { n: m.errors })}</span>}
                    {m.errors === 0 && m.warnings > 0 && <span className="badge warn">{tn(m.warnings, '{n} aviso', '{n} avisos')}</span>}
                  </div>
                  <div className="faint" style={{ fontSize: 13, marginTop: 2 }}>
                    {[
                      m.category || t('Sin categoría'),
                      tn(m.counts.colors, '{n} color', '{n} colores'),
                      tn(m.counts.frames, '{n} foto 360', '{n} fotos 360'),
                      tn(m.counts.specials, '{n} especial', '{n} especiales'),
                      t('{n} en galería', { n: m.counts.gallery }),
                      t('{n} de 3 PDF', { n: m.counts.documents }),
                    ].join(' · ')}
                  </div>
                </div>
                <div className="actions">
                  <button
                    className="btn btn-sm btn-ghost btn-icon"
                    title={m.visible ? t('Ocultar del sitio') : t('Mostrar en el sitio')}
                    onClick={() =>
                      act(() => patch(`/admin/models/${m.id}`, { visible: !m.visible }), m.visible ? t('{name} quedó oculta', { name: m.name }) : t('{name} quedó visible', { name: m.name }))
                    }
                  >
                    {m.visible ? <IconEye /> : <IconEyeOff />}
                  </button>
                  <button
                    className="btn btn-sm btn-ghost btn-icon"
                    title={t('Destacar en el carrusel')}
                    disabled={m.featured}
                    onClick={() => act(() => post(`/admin/models/${m.id}/featured`), t('{name} es la destacada', { name: m.name }))}
                  >
                    <IconStar />
                  </button>
                  <button className="btn btn-sm btn-ghost btn-icon" title={t('Duplicar')} onClick={() => setCreating({ from: m })}>
                    <IconCopy />
                  </button>
                  {me?.role === 'ADMIN' && (
                    <button
                      className="btn btn-sm btn-ghost btn-icon"
                      title={t('Eliminar')}
                      onClick={async () => {
                        const ok = await confirm(
                          tr('¿Eliminar {name}? Se borran sus textos, colores, especiales y galería. Las fotos quedan guardadas.', { name: <strong>{m.name}</strong> }),
                          { confirmLabel: t('Eliminar'), danger: true },
                        );
                        if (ok) await act(() => del(`/admin/models/${m.id}`), t('{name} eliminada', { name: m.name }));
                      }}
                    >
                      <IconTrash />
                    </button>
                  )}
                  <Link to={`/motos/${m.id}`} className="btn btn-sm">
                    {t('Editar')}
                  </Link>
                </div>
              </div>
            )}
          </SortableList>
        </div>
      )}

      {creating && <CreateModelDialog from={creating.from} onClose={() => setCreating(null)} />}
    </>
  );
}

function CreateModelDialog({ from, onClose }: { from?: ModelListItem; onClose: () => void }) {
  const { t } = useT();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [name, setName] = useState(from ? `${from.name} ` : '');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  const effectiveSlug = slugTouched ? slug : slugify(name);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = { name: name.trim().toUpperCase(), slug: effectiveSlug };
      const { model } = from
        ? await post<{ model: BikeModel }>(`/admin/models/${from.id}/duplicate`, body)
        : await post<{ model: BikeModel }>('/admin/models', body);
      await qc.invalidateQueries({ queryKey: ['models'] });
      navigate(`/motos/${model.id}`);
    } catch (e) {
      setError(e instanceof ApiError ? e : new ApiError(0, errorMessage(e)));
      setBusy(false);
    }
  };

  return (
    <Modal
      title={from ? t('Duplicar {name}', { name: from.name }) : t('Nueva moto')}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            {t('Cancelar')}
          </button>
          <button className="btn btn-primary" disabled={busy || !name.trim() || !effectiveSlug} onClick={submit}>
            {from ? t('Duplicar') : t('Crear')}
          </button>
        </>
      }
    >
      {from && (
        <p className="muted" style={{ margin: 0 }}>
          {t('Se copian los textos, la ficha, los colores con sus fotos, los especiales y la galería. La copia queda oculta.')}
        </p>
      )}
      {!from && (
        <p className="muted" style={{ margin: 0 }}>
          {t('La moto se crea oculta: complétala y hazla visible cuando esté lista.')}
        </p>
      )}
      {error && !error.errors.length && <div className="notice error">{error.message}</div>}
      <Field label={t('Nombre')} hint={t('Como aparece en el sitio, p. ej. RUSH 150')} error={error?.field('name')} count={{ value: name.trim(), max: 14 }}>
        {(id) => <input id={id} className="input" value={name} onChange={(e) => setName(e.target.value)} autoFocus />}
      </Field>
      <Field label={t('Dirección en el sitio')} hint={`/productos/${effectiveSlug || '…'}/`} error={error?.field('slug')}>
        {(id) => (
          <input
            id={id}
            className="input"
            value={effectiveSlug}
            onChange={(e) => {
              setSlugTouched(true);
              setSlug(slugify(e.target.value));
            }}
          />
        )}
      </Field>
    </Modal>
  );
}
